import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

interface Reflection {
  number: string;
  title: string;
  side: 'candidate' | 'interviewer' | 'both';
  content: React.ReactNode;
  pullQuote?: string;
}

const reflections: Reflection[] = [
  {
    number: "01",
    title: "The Entrance Exam Paradox",
    side: 'both',
    content: (
      <>
        <p className="mb-4">
          After hundreds of interviews on both sides, one truth became unavoidable:
          we've built an industry where the entrance exam has almost nothing to do with the actual job.
        </p>
        <p className="mb-4">
          As a candidate, I spent weeks solving dynamic programming problems. On the job? I've used DP exactly once in 7 years. And I Googled the solution anyway.
        </p>
        <p>
          As an interviewer, I've seen people stumble on binary tree questions in interviews, then go on to build systems that handle millions of requests. The two skills barely overlap.
        </p>
      </>
    ),
    pullQuote: "The interview tests your ability to interview. The job tests your ability to build.",
  },
  {
    number: "02",
    title: "What I Actually Look For Now",
    side: 'interviewer',
    content: (
      <>
        <p className="mb-4">
          After years of conducting interviews that felt pointless, I changed what I look for:
        </p>
        <p className="mb-4">
          <strong>Can they debug?</strong> I show broken code and watch how they think. Do they read the error message first? Do they form a hypothesis before changing things randomly?
        </p>
        <p className="mb-4">
          <strong>Can they communicate?</strong> When they don't know something, do they admit it or try to bluff through? Can they explain their reasoning as they go?
        </p>
        <p>
          <strong>Are they curious?</strong> Do they ask questions about the system they'd be working on? Do they seem genuinely interested, or just trying to get through the interview?
        </p>
      </>
    ),
    pullQuote: "The best signal is how they approach problems they don't immediately know how to solve.",
  },
  {
    number: "03",
    title: "Stress Tests Are Useless",
    side: 'interviewer',
    content: (
      <>
        <p className="mb-4">
          Some interviewers try to "stress test" candidates. Rapid-fire questions. Interruptions. Watching how they handle pressure.
        </p>
        <p className="mb-4">
          I used to think this was useful. Then I noticed something: the candidates who stayed calm under artificial interview pressure weren't necessarily the ones who stayed calm during actual production incidents. Different skill entirely.
        </p>
        <p>
          Now I try to make candidates comfortable. Give context. Let them think. Ask follow-up questions instead of gotchas. The information I get is much more useful.
        </p>
      </>
    ),
    pullQuote: "You're hiring who they'll be on day 100, not who they are during their most stressful hour.",
  },
  {
    number: "04",
    title: "The Opportunity Cost",
    side: 'candidate',
    content: (
      <>
        <p className="mb-4">
          I've spent roughly 400 hours preparing for technical interviews across my career. That's 50 full work days. Ten work weeks.
        </p>
        <p className="mb-4">
          What could I have built instead? Contributed to open source? Learned actual system design by building systems? Written documentation? Mentored someone?
        </p>
        <p>
          The cruel part: the skills that make you good at interviews (pattern recognition, performing under artificial time limits) are almost useless on the job. And the skills that make you good at the job (patience, collaboration, deep system knowledge) barely get tested.
        </p>
      </>
    ),
    pullQuote: "Every hour on LeetCode is an hour not spent becoming a better engineer.",
  },
  {
    number: "05",
    title: "It's Pattern Recognition, Not Problem Solving",
    side: 'candidate',
    content: (
      <>
        <p className="mb-4">
          Here's what took me years to understand: technical interviews don't test problem-solving. They test whether you've seen this specific pattern before.
        </p>
        <p className="mb-4">
          If you recognize the pattern, you look brilliant. If you don't, you look incompetent. The actual skill of breaking down unfamiliar problems? Barely measured.
        </p>
        <p>
          I've seen candidates solve "hard" problems in three minutes because they'd practiced that exact pattern the night before. That's not intelligence. That's preparation theater.
        </p>
      </>
    ),
  },
  {
    number: "06",
    title: "We Have No Idea If This Works",
    side: 'interviewer',
    content: (
      <>
        <p className="mb-4">
          Here's something that bothers me: we have almost no data on whether technical interviews actually predict job performance.
        </p>
        <p className="mb-4">
          We track who we hire and how they do. But we never see what happened to the people we rejected. Maybe some of them would have been great. We'll never know.
        </p>
        <p>
          So we keep making interviews harder, patting ourselves on the back for our "high bar," with zero evidence that we're selecting for the right things.
        </p>
      </>
    ),
    pullQuote: "We're optimizing a system where we only see half the outcomes.",
  },
  {
    number: "07",
    title: "Everyone Is Having a Bad Day",
    side: 'interviewer',
    content: (
      <>
        <p className="mb-4">
          Something I try to remember: every candidate is having one of their most stressful days. They might be interviewing while working full-time (exhausted). They might be between jobs (anxious about money). They might have been rejected five times already this month.
        </p>
        <p className="mb-4">
          I started asking different questions. "Tell me about something you built that you're proud of." "What's a bug that took you forever to figure out?" "What would you do differently if you could start that project over?"
        </p>
        <p>
          People open up when you treat them like a person instead of a test subject.
        </p>
      </>
    ),
  },
  {
    number: "08",
    title: "What Actually Matters On The Job",
    side: 'both',
    content: (
      <>
        <p className="mb-4">
          After years of watching what makes engineers effective, here's what I've noticed:
        </p>
        <p className="mb-4">
          <strong>Reading code matters more than writing it.</strong> Most of your time is spent understanding what already exists. <strong>Communication matters more than being clever.</strong> The engineer who can explain their work gets support, resources, and eventually promoted.
        </p>
        <p>
          <strong>Curiosity matters more than current knowledge.</strong> Technology changes constantly. The ability to learn doesn't. <strong>Staying calm when things break matters a lot.</strong> Production incidents happen. Deadlines slip. Plans change.
        </p>
      </>
    ),
    pullQuote: "We test for algorithm speed. We need adaptability and communication.",
  },
];

const InterviewParadoxStudy: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const { scrollYProgress } = useScroll();
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  const getSideLabel = (side: string) => {
    switch (side) {
      case 'candidate': return 'as candidate';
      case 'interviewer': return 'as interviewer';
      case 'both': return 'both sides';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f6f1]">
      {/* Warm subtle texture */}
      <div
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Progress bar */}
      <motion.div
        className="fixed top-0 left-0 h-0.5 bg-[#c4a484] z-50"
        style={{ width: progressWidth }}
      />

      {/* Back button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/labs')}
        className="fixed top-8 left-8 z-40 flex items-center gap-2 text-[#8b7355] hover:text-[#5c4d3d] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back</span>
      </motion.button>

      {/* Hero */}
      <section className="min-h-[80vh] flex flex-col justify-end px-8 pb-20 pt-32 relative">
        <div className="max-w-4xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1 }}
          >
            <p className="text-[#a08060] text-sm tracking-widest uppercase mb-6">
              Notes from both sides of the table
            </p>

            <h1 className="text-6xl md:text-8xl font-light text-[#3d3428] mb-8 leading-[0.9] tracking-tight">
              The Interview
              <br />
              <span className="italic">Paradox</span>
            </h1>

            <div className="max-w-xl">
              <p className="text-xl text-[#5c4d3d] leading-relaxed mb-8">
                After giving and taking hundreds of technical interviews, I've learned that most of what we test has nothing to do with what we need.
              </p>

              <div className="h-px bg-[#d4c4b0] w-24"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Opening quote */}
      <section className="py-20 px-8">
        <div className="max-w-3xl mx-auto">
          <motion.blockquote
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-2xl md:text-3xl text-[#3d3428] leading-relaxed font-light italic">
              "We spend months mastering skills we'll never use, to get jobs where we'll use skills we never practiced."
            </p>
          </motion.blockquote>
        </div>
      </section>

      {/* Context */}
      <section className="py-12 px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="border-l-2 border-[#d4c4b0] pl-6"
          >
            <p className="text-[#5c4d3d] leading-relaxed mb-4">
              This isn't a rant about broken interviews. It's observations from 7+ years on both sides. As a candidate doing LeetCode at 2am. As an interviewer trying to figure out who would actually be good at the job.
            </p>
            <p className="text-[#8b7355] leading-relaxed text-sm">
              Some of these thoughts contradict each other. That's kind of the point.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reflections */}
      <section className="py-16 px-8">
        <div className="max-w-2xl mx-auto">
          {reflections.map((reflection, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="mb-20 last:mb-0"
            >
              {/* Header */}
              <header className="mb-6">
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-[#c4a484] text-sm font-light">{reflection.number}</span>
                  <span className="text-[#a08060] text-xs uppercase tracking-wider">{getSideLabel(reflection.side)}</span>
                </div>
                <h2 className="text-2xl md:text-3xl text-[#3d3428] font-light leading-tight">
                  {reflection.title}
                </h2>
              </header>

              {/* Content */}
              <div className="text-[#5c4d3d] leading-[1.8] text-lg">
                {reflection.content}
              </div>

              {/* Pull quote */}
              {reflection.pullQuote && (
                <div className="mt-8 py-6 border-t border-b border-[#e8ddd0]">
                  <p className="text-[#3d3428] text-xl font-light italic text-center">
                    {reflection.pullQuote}
                  </p>
                </div>
              )}
            </motion.article>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="py-12 px-8">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-4">
          <div className="h-px bg-[#d4c4b0] flex-1"></div>
          <span className="text-[#c4a484] text-2xl">✦</span>
          <div className="h-px bg-[#d4c4b0] flex-1"></div>
        </div>
      </div>

      {/* Big Quote */}
      <section className="py-16 px-8 bg-[#3d3428]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <blockquote className="text-2xl md:text-4xl text-[#f9f6f1] leading-relaxed font-light mb-6">
              "We've built an industry where the entrance exam has nothing to do with the actual job."
            </blockquote>
            <cite className="text-[#a08060] text-sm">
              Something every senior engineer realizes eventually
            </cite>
          </motion.div>
        </div>
      </section>

      {/* What I Try Now */}
      <section className="py-20 px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl text-[#3d3428] font-light mb-2">What I Try Now</h2>
            <div className="h-px bg-[#d4c4b0] w-16"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-[#a08060] text-xs uppercase tracking-widest mb-6">When interviewing</h3>
              <ul className="space-y-4">
                {[
                  "Share the problem ahead of time when I can",
                  "Let people use Google. They will on the job anyway",
                  "Ask about things they've actually built",
                  "Pay attention to how they think, not just answers",
                  "Give feedback even when we don't make an offer",
                ].map((item, i) => (
                  <li key={i} className="text-[#5c4d3d] leading-relaxed pl-4 border-l border-[#d4c4b0]">
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-[#a08060] text-xs uppercase tracking-widest mb-6">When being interviewed</h3>
              <ul className="space-y-4">
                {[
                  "Ask what a typical day actually looks like",
                  "Interview them as much as they're interviewing me",
                  "Be honest when I don't know something",
                  "Ask about the team, not just the technology",
                  "Remember that rejection isn't always bad news",
                ].map((item, i) => (
                  <li key={i} className="text-[#5c4d3d] leading-relaxed pl-4 border-l border-[#d4c4b0]">
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-[#5c4d3d] text-lg leading-relaxed mb-8">
              I don't have a solution. Predicting job performance from a few hours of conversation is genuinely hard.
            </p>
            <p className="text-[#5c4d3d] text-lg leading-relaxed mb-8">
              But we can be more honest about what we're actually testing. More humble about what we're missing. More respectful to the people sitting across from us.
            </p>
            <p className="text-[#3d3428] text-xl italic">
              The best interviews I've been part of felt like conversations, not exams.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <section className="py-12 px-8 border-t border-[#e8ddd0]">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#a08060] text-sm">
            If you're preparing for interviews right now: good luck. It's a strange game, but you can get through it.
          </p>
        </div>
      </section>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="interview-paradox" variant="light" />
    </div>
  );
};

export default InterviewParadoxStudy;
